/**
 * ResolvedTemplatePreview — единый вывод предпросмотра шаблона после подстановки переменных.
 * Дата: 17.03.2026 - Общая основа для Templates и Manual Broadcasts с поддержкой WABA preview.
 */
import { Badge } from '@/components/ui';
import { getChannelLabel } from '@/config/channels';
import type { TemplateButton, WABAPreview } from '@/api/templates';
import { WABAButtonsPreview } from './WABAButtonsPreview';

interface ResolvedTemplatePreviewProps {
  preview: string;
  wabaPreview?: WABAPreview | null;
  buttons?: TemplateButton[];
  variablesMissing?: string[];
  channel?: string;
  isWabaTemplate?: boolean;
  testId?: string;
}

export function ResolvedTemplatePreview({
  preview,
  wabaPreview,
  buttons,
  variablesMissing,
  channel,
  isWabaTemplate = false,
  testId,
}: ResolvedTemplatePreviewProps) {
  return (
    <div className="space-y-4" data-testid={testId}>
      {channel && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{getChannelLabel(channel)}</Badge>
          {isWabaTemplate && <Badge variant="secondary">WABA Template</Badge>}
        </div>
      )}

      {isWabaTemplate && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="mb-2 text-xs text-emerald-700">
            17.03.2026 - Для WABA показываем Meta/Wazzup preview на той же основе, что и в templates preview.
          </div>
          {wabaPreview ? (
            <div className="space-y-2 rounded border bg-white p-3">
              {wabaPreview.header_text && (
                <div className="font-semibold">{wabaPreview.header_text}</div>
              )}
              <div className="whitespace-pre-wrap text-sm">{wabaPreview.body_text || preview}</div>
              {wabaPreview.footer_text && (
                <div className="text-xs text-neutral-500">{wabaPreview.footer_text}</div>
              )}
              {wabaPreview.buttons && wabaPreview.buttons.length > 0 && (
                <WABAButtonsPreview buttons={wabaPreview.buttons} testId="resolved-preview-waba-buttons" />
              )}
            </div>
          ) : (
            <div className="text-sm text-neutral-600">
              WABA-шаблон хранится в Meta/Wazzup; локально показываем доступный snapshot рендера.
            </div>
          )}
        </div>
      )}

      <div>
        <div className="mb-2 text-sm font-medium text-neutral-700">Результат</div>
        <div className="rounded border bg-neutral-50 p-4 whitespace-pre-wrap text-sm text-neutral-900">
          {preview || '—'}
        </div>
      </div>

      {!isWabaTemplate && buttons && buttons.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-neutral-700">Inline-кнопки</div>
          <div className="flex flex-wrap gap-2">
            {buttons.map((button, index) => (
              <div
                key={`${button.type}-${button.text}-${index}`}
                className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
              >
                <div className="font-medium">{button.text}</div>
                <div className="mt-1 text-blue-700">
                  {button.type === 'url' && button.url
                    ? button.url
                    : button.callback_data || button.type}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {variablesMissing && variablesMissing.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-neutral-700">Отсутствующие переменные</div>
          <div className="flex flex-wrap gap-1">
            {variablesMissing.map((value) => (
              <Badge key={value} variant="error">{value}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
